import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { pricingApiConnect } from '../../../lib/api/pricingApi.connect';
import { projectDataClient } from '../../../lib/connect/clients';
import { usePriceDocumentMetadata } from '../v2/pricing/usePriceVolume';
function WorkbookLabels({ projectId, documentId, original, revised, principal }) {
    const documents = useQuery({
        queryKey: [
            'pricing-workbook-labels',
            principal,
            projectId
        ],
        retry: false,
        queryFn: async ()=>{
            const { volumes } = await projectDataClient.listProjectVolumes({
                projectId
            });
            return (await Promise.all(volumes.map((v)=>projectDataClient.listVolumeDocuments({
                    projectId,
                    volumeId: v.volumeId
                })))).flatMap((r)=>r.documents);
        }
    });
    const doc = documents.data?.find((d)=>d.shirohigeDocumentId === documentId);
    const metadata = usePriceDocumentMetadata(projectId, doc, principal);
    const version = (id)=>{
        const found = metadata.data?.versions.find((v)=>v.id === id);
        return found ? `v${found.versionNumber}` : 'version metadata unavailable';
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            doc?.label || doc?.submissionFilename || 'Spreadsheet metadata unavailable',
            " —",
            ' ',
            version(original),
            revised ? ` → ${version(revised)}` : ', no revision'
        ]
    });
}
function parsePins(raw) {
    try {
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch  {
        return [];
    }
}
export function PricingEvidencePanel({ projectId, pricingId, canEdit = true, freezeDisabled = false, onFreezeRequest, cacheScope = [] }) {
    const priceVolumeEnabled = useFeatureFlag('proposal_pricing_v2') === true;
    const queryClient = useQueryClient();
    const key = cacheScope.length ? [
        'pricing-evidence',
        cacheScope[0],
        projectId,
        pricingId,
        cacheScope[1]
    ] : [
        'pricing-evidence',
        projectId,
        pricingId
    ];
    const { data, error } = useQuery({
        queryKey: key,
        staleTime: cacheScope.length ? 0 : undefined,
        queryFn: ()=>pricingApiConnect.listEvidence(projectId, pricingId)
    });
    const refresh = ()=>{
        if (cacheScope.length) for (const name of [
            'pricing',
            'lcats',
            'proposal-project',
            'pricing-ratebook',
            'pricing-ratebook-row',
            'pricing-totals',
            'pricing-detail',
            'proposal-home',
            'proposal-record-header',
            'projectActivity',
            'rate selections'
        ])void queryClient.invalidateQueries({
            queryKey: [
                name
            ]
        });
        queryClient.invalidateQueries({
            queryKey: key
        });
        queryClient.invalidateQueries({
            queryKey: [
                'pricing',
                projectId,
                pricingId
            ]
        });
    };
    const freeze = useMutation({
        mutationFn: ()=>pricingApiConnect.snapshotVersion(projectId, pricingId),
        onSuccess: refresh
    });
    const resolve = useMutation({
        mutationFn: (v)=>pricingApiConnect.resolveProposal(projectId, v.proposalId, v.accept),
        onSuccess: refresh
    });
    const versions = data?.versions ?? [];
    const workbooks = data?.workbooks ?? [];
    const pending = (data?.proposals ?? []).filter((p)=>p.status === 'PENDING');
    const failure = error ?? freeze.error ?? resolve.error;
    return /*#__PURE__*/ _jsxs("section", {
        className: "border border-border rounded-lg p-4 space-y-4",
        "data-testid": Testids.pricingEvidence.panel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-semibold",
                        children: "Pricing evidence"
                    }),
                    canEdit && /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        variant: "outline",
                        "data-testid": Testids.pricingEvidence.freeze,
                        onClick: ()=>onFreezeRequest ? onFreezeRequest() : freeze.mutate(),
                        disabled: freeze.isPending || freezeDisabled,
                        children: "Freeze version"
                    })
                ]
            }),
            failure && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-danger",
                children: failure.message
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: "Versions and pinned Rate Book selections"
                    }),
                    versions.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No frozen versions yet."
                    }),
                    versions.map((v)=>/*#__PURE__*/ _jsxs("div", {
                            className: "text-sm",
                            "data-testid": Testids.pricingEvidence.version,
                            "data-version-no": v.versionNo,
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium",
                                    children: [
                                        "v",
                                        v.versionNo
                                    ]
                                }),
                                v.frozen ? ' (frozen)' : '',
                                parsePins(v.pinnedSelectionsJson).length === 0 ? /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: " — no Rate Book selections pinned"
                                }) : /*#__PURE__*/ _jsx("ul", {
                                    className: "ml-4 list-disc",
                                    children: parsePins(v.pinnedSelectionsJson).map((pin)=>/*#__PURE__*/ _jsxs("li", {
                                            "data-testid": Testids.pricingEvidence.pin,
                                            children: [
                                                pin.consumer_ref,
                                                ": observation ",
                                                pin.observation_id,
                                                " v",
                                                pin.observation_version,
                                                ' ',
                                                "@ $",
                                                pin.observation_amount
                                            ]
                                        }, `${pin.consumer_ref}-${pin.observation_id}`))
                                })
                            ]
                        }, v.versionId))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: "Workbooks"
                    }),
                    workbooks.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No workbooks registered."
                    }),
                    workbooks.map((w)=>/*#__PURE__*/ _jsxs("div", {
                            className: "text-sm",
                            "data-testid": Testids.pricingEvidence.workbook,
                            "data-workbook-id": w.workbookId,
                            "data-status": w.reconciliationStatus,
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: priceVolumeEnabled ? /*#__PURE__*/ _jsx(WorkbookLabels, {
                                        projectId: projectId,
                                        documentId: w.shirohigeDocumentId || w.artifactId,
                                        original: w.originalVersionId,
                                        revised: w.revisedVersionId,
                                        principal: cacheScope[0] || 'pricing'
                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            "Workbook ",
                                            w.artifactId,
                                            " — original version ",
                                            w.originalVersionId,
                                            w.revisedVersionId ? `, revised version ${w.revisedVersionId}` : ', no revision'
                                        ]
                                    })
                                }),
                                w.reconciliationStatus === 'MAPPED' ? /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: "Connected to the model"
                                }) : /*#__PURE__*/ _jsx("span", {
                                    className: "text-warning",
                                    "data-testid": Testids.pricingEvidence.unavailable,
                                    children: "Reconciliation unavailable — this workbook is not connected to the model"
                                })
                            ]
                        }, w.workbookId))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: "Pending reconciliation proposals"
                    }),
                    pending.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No pending proposals."
                    }),
                    pending.map((p)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between text-sm",
                            "data-testid": Testids.pricingEvidence.proposal,
                            "data-proposal-id": p.proposalId,
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        priceVolumeEnabled ? /*#__PURE__*/ _jsx(WorkbookLabels, {
                                            projectId: projectId,
                                            documentId: workbooks.find((w)=>w.workbookId === p.workbookId)?.shirohigeDocumentId || '',
                                            original: p.originalWorkbookVersionId,
                                            revised: p.revisedWorkbookVersionId,
                                            principal: cacheScope[0] || 'pricing'
                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                "Workbook version ",
                                                p.originalWorkbookVersionId,
                                                " → ",
                                                p.revisedWorkbookVersionId
                                            ]
                                        }),
                                        ' ',
                                        "(PENDING)"
                                    ]
                                }),
                                canEdit && /*#__PURE__*/ _jsxs("span", {
                                    className: "flex gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            "data-testid": Testids.pricingEvidence.accept,
                                            disabled: resolve.isPending,
                                            onClick: ()=>resolve.mutate({
                                                    proposalId: p.proposalId,
                                                    accept: true
                                                }),
                                            children: "Accept"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "outline",
                                            "data-testid": Testids.pricingEvidence.reject,
                                            disabled: resolve.isPending,
                                            onClick: ()=>resolve.mutate({
                                                    proposalId: p.proposalId,
                                                    accept: false
                                                }),
                                            children: "Reject"
                                        })
                                    ]
                                })
                            ]
                        }, p.proposalId))
                ]
            })
        ]
    });
}
